// Sprint 3 Slice 1 — in-place hotspot property editor.
//
// Slide-in right panel (mirrors materialPanel.ts visual conventions) that
// lets the operator edit a single hotspot's fields live. Updates fire on
// every input change so the floating HTML label + dot reposition without
// a "save" round-trip — the in-memory RoomsConfig is the source of truth
// until Export rooms.json is clicked from editorMode.
//
// Only shown when ?edit=1 is in the URL (gated by App + editorMode).

import type { Hotspot, HotspotType, Room } from '../app/types';
import { isBadNavTarget } from '../app/editorProblems';
import { showToast } from './toast';

export type HotspotChangeHandler = (hotspot: Hotspot) => void;
export type HotspotDeleteHandler = (hotspot: Hotspot) => void;
/** Live view of the rooms in the active config, for the navigation picker. */
export type RoomListProvider = () => readonly Room[];

/** What a picked info-hotspot image resolves to. `assetPath: null` = cleared. */
export interface PickedImage {
  assetPath: string | null;
  url: string | null;
}

/**
 * Ask the host to supply an image for an info hotspot.
 *
 * Present only in embed mode, where the portal owns storage. Its absence is
 * what makes the local `?edit=1` editor fall back to a plain URL field: a
 * standalone operator has a filesystem and a demos/ folder, and no session to
 * upload with.
 */
export type ImagePicker = (hotspot: Hotspot) => Promise<PickedImage>;

/** Portal-owned pointer that outlives the signed URL. Mirrors
 *  INFO_IMAGE_PATH_KEY in the portal's lib/portal/tour-config.ts. */
const IMAGE_ASSET_PATH_KEY = 'imageAssetPath';

export class EditorPanel {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly getRooms: RoomListProvider;
  private current: Hotspot | null = null;
  private onChangeCb: HotspotChangeHandler = () => {};
  private onDeleteCb: HotspotDeleteHandler = () => {};
  private readonly pickImage: ImagePicker | null;

  constructor(getRooms: RoomListProvider = () => [], pickImage: ImagePicker | null = null) {
    this.getRooms = getRooms;
    this.pickImage = pickImage;
    // Mount dynamically so the slice doesn't touch index.html. Single
    // top-level aside; the body content is rebuilt on each openFor() call
    // because field set varies by hotspot type.
    this.root = document.createElement('aside');
    this.root.id = 'editor-panel';
    this.root.className = 'side-panel editor-panel';
    this.root.setAttribute('aria-label', 'Editar hotspot');
    this.root.innerHTML = `
      <header class="editor-panel__header">
        <div>
          <h2>Editar hotspot</h2>
          <p class="editor-panel__hint">Salve com "Exportar rooms.json" no toolbar.</p>
        </div>
        <button type="button" class="modal__close editor-panel__close" aria-label="Fechar">×</button>
      </header>
      <div class="editor-panel__body"></div>
    `;
    document.body.appendChild(this.root);
    this.body = this.root.querySelector('.editor-panel__body') as HTMLElement;
    (this.root.querySelector('.editor-panel__close') as HTMLButtonElement).addEventListener(
      'click',
      () => this.close(),
    );
  }

  onChange(handler: HotspotChangeHandler): void {
    this.onChangeCb = handler;
  }

  onDelete(handler: HotspotDeleteHandler): void {
    this.onDeleteCb = handler;
  }

  openFor(hotspot: Hotspot): void {
    this.current = hotspot;
    this.render();
    this.root.classList.add('is-open');
  }

  /**
   * Re-render the panel's inputs IF the given hotspot is the one currently
   * shown. Used by App during a drag-to-reposition session so the yaw/pitch
   * number fields track the live values without losing the visible panel.
   * No-op if the panel isn't open or is showing a different hotspot.
   */
  refreshFor(hotspot: Hotspot): void {
    if (!this.isOpen() || this.current?.id !== hotspot.id) return;
    this.current = hotspot;
    this.render();
  }

  close(): void {
    this.root.classList.remove('is-open');
    this.current = null;
  }

  isOpen(): boolean {
    return this.root.classList.contains('is-open');
  }

  // ── Internals ─────────────────────────────────────────────────

  private render(): void {
    if (!this.current) return;
    const h = this.current;
    this.body.innerHTML = '';
    // Shared fields across every hotspot type
    this.body.appendChild(this.fieldRow('Tipo', this.typeSelect(h)));
    this.body.appendChild(this.fieldRow('Label', this.textInput(h.label, (v) => this.patch({ label: v }))));
    this.body.appendChild(
      this.fieldRow('Yaw (°)', this.numberInput(h.yaw, -180, 180, 0.5, (v) => this.patch({ yaw: v }))),
    );
    this.body.appendChild(
      this.fieldRow('Pitch (°)', this.numberInput(h.pitch, -89, 89, 0.5, (v) => this.patch({ pitch: v }))),
    );
    this.body.appendChild(
      this.fieldRow(
        'Distance (m)',
        this.numberInput(h.distance ?? 12, 1, 50, 0.5, (v) => this.patch({ distance: v })),
      ),
    );
    // Type-specific fields
    switch (h.type) {
      case 'info':
        this.body.appendChild(
          this.fieldRow(
            'Description',
            this.textareaInput(h.description, (v) => this.patch({ description: v })),
          ),
        );
        // Two authoring paths for the same two fields. Embedded in the portal
        // the operator has no filesystem to point at, only a session that can
        // upload; standalone they have a demos/ folder and no session. The
        // picker's presence is the whole discriminator.
        this.body.appendChild(
          this.pickImage
            ? this.fieldRow('Imagem', this.imagePickerControl(h))
            : this.fieldRow(
                'Imagem (URL)',
                // Blank means "no picture", so an emptied field must clear the
                // key rather than store '' — an empty src resolves to the page
                // URL and renders a broken image.
                this.textInput(h.imageUrl ?? '', (v) =>
                  this.patch({ imageUrl: v.trim() === '' ? undefined : v.trim() }),
                ),
              ),
        );
        this.body.appendChild(
          this.fieldRow(
            'Imagem (alt)',
            this.textInput(h.imageAlt ?? '', (v) =>
              this.patch({ imageAlt: v.trim() === '' ? undefined : v }),
            ),
          ),
        );
        break;
      case 'navigation': {
        this.body.appendChild(
          this.fieldRow(
            'Sala de destino',
            this.roomSelect(h.targetRoomId, (v) => this.patch({ targetRoomId: v })),
          ),
        );
        if (isBadNavTarget({ rooms: this.getRooms() } as never, h.targetRoomId)) {
          const warn = document.createElement('p');
          warn.className = 'editor-panel__warning';
          warn.textContent = 'Escolha a sala de destino — o tour não pode ser publicado com uma seta sem destino.';
          this.body.appendChild(warn);
        }
        break;
      }
      case 'variant':
        this.body.appendChild(
          this.fieldRow(
            'Target variant id',
            this.textInput(h.targetVariantId, (v) => this.patch({ targetVariantId: v })),
          ),
        );
        break;
      case 'cta':
        this.body.appendChild(
          this.fieldRow(
            'CTA label',
            this.textInput(h.ctaLabel, (v) => this.patch({ ctaLabel: v })),
          ),
        );
        this.body.appendChild(
          this.fieldRow(
            'CTA URL',
            this.textInput(h.ctaUrl ?? '', (v) => this.patch({ ctaUrl: v || undefined })),
          ),
        );
        break;
      case 'video':
        this.body.appendChild(
          this.fieldRow(
            'Video URL',
            this.textInput(h.videoUrl ?? '', (v) => this.patch({ videoUrl: v || undefined })),
          ),
        );
        break;
      case 'surface':
        // Surface swatches edit best as JSON for Slice 1 — full UI in Slice 3+.
        this.body.appendChild(
          this.fieldRow(
            'Swatches (JSON)',
            this.textareaInput(JSON.stringify(h.swatches, null, 2), (v) => {
              try {
                this.patch({ swatches: JSON.parse(v) });
              } catch {
                // Silently ignore malformed JSON — the input stays editable.
              }
            }),
          ),
        );
        break;
    }
    // Read-only id at the bottom plus a delete button.
    const id = document.createElement('p');
    id.className = 'editor-panel__id';
    id.textContent = `id: ${h.id}`;
    this.body.appendChild(id);
    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'editor-panel__delete';
    del.textContent = 'Excluir hotspot';
    del.addEventListener('click', () => {
      if (!this.current) return;
      this.onDeleteCb(this.current);
      this.close();
    });
    this.body.appendChild(del);
  }

  private patch(partial: Partial<Hotspot>): void {
    if (!this.current) return;
    // The discriminated union prevents a generic Partial<Hotspot> assign
    // through the type system; the runtime mutation is safe because the
    // form only emits keys that exist on the active variant.
    Object.assign(this.current, partial);
    this.onChangeCb(this.current);
  }

  private typeSelect(h: Hotspot): HTMLSelectElement {
    const sel = document.createElement('select');
    sel.className = 'editor-panel__input';
    const types: HotspotType[] = ['info', 'navigation', 'variant', 'cta', 'video', 'surface'];
    for (const t of types) {
      const opt = document.createElement('option');
      opt.value = t;
      opt.textContent = t;
      if (t === h.type) opt.selected = true;
      sel.appendChild(opt);
    }
    sel.addEventListener('change', () => {
      if (!this.current) return;
      // Changing type fundamentally reshapes the hotspot — wipe type-only
      // fields and add minimal stubs so the discriminated union is valid.
      const next = morphHotspotType(this.current, sel.value as HotspotType);
      Object.keys(this.current).forEach((k) =>
        delete (this.current as unknown as Record<string, unknown>)[k],
      );
      Object.assign(this.current, next);
      this.onChangeCb(this.current);
      this.render(); // re-render to show the new type's fields
    });
    return sel;
  }

  /**
   * Room picker for a navigation target.
   *
   * This is a select rather than a text field because `targetRoomId` is a
   * foreign key: validate-rooms rejects any value that isn't a room `id`, and
   * a free-text box invites the operator to type the room's display *name*
   * ("Room 2") instead of its id ("room-2") — which authors six dangling
   * links that only surface much later, at publish time.
   *
   * A value matching no room is kept as a flagged option rather than being
   * silently coerced to the first room: the config is already wrong at that
   * point, and quietly repointing a link is a worse failure than showing it.
   */
  private roomSelect(value: string, onChange: (v: string) => void): HTMLSelectElement {
    const sel = document.createElement('select');
    sel.className = 'editor-panel__input';
    const rooms = this.getRooms();
    const known = rooms.some((r) => r.id === value);
    if (!known) {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = value ? `⚠️ "${value}" — sala inexistente` : '— selecione —';
      opt.selected = true;
      sel.appendChild(opt);
    }
    for (const room of rooms) {
      const opt = document.createElement('option');
      opt.value = room.id;
      // Both parts shown: the operator thinks in names, the file stores ids.
      opt.textContent = room.name === room.id ? room.id : `${room.name} (${room.id})`;
      if (room.id === value) opt.selected = true;
      sel.appendChild(opt);
    }
    // An unfinished destination is STYLED as unfinished. "— selecione —" is a
    // plausible resting state to look at, which is how nineteen of these got
    // saved in one session; the class turns the field red and the row carries
    // a message saying the tour cannot be published while it is blank.
    if (!known) sel.classList.add('editor-panel__input--invalid');
    sel.addEventListener('change', () => {
      onChange(sel.value);
      const nowKnown = !isBadNavTarget({ rooms: this.getRooms() } as never, sel.value);
      sel.classList.toggle('editor-panel__input--invalid', !nowKnown);
    });
    return sel;
  }

  /**
   * Thumbnail + "choose" + "remove", for embed mode.
   *
   * The button is disabled while a request is outstanding because there is no
   * useful second request: the portal shows a modal over the whole editor, and
   * a second click would open a second one behind it. It is re-enabled on
   * every exit path including rejection, so a portal that never answers costs
   * a wait, not a dead control.
   */
  private imagePickerControl(hotspot: Hotspot & { type: 'info' }): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'editor-panel__image';

    const preview = document.createElement('img');
    preview.className = 'editor-panel__image-preview';
    preview.alt = '';
    const paint = (url: string | undefined): void => {
      if (url) {
        preview.src = url;
        preview.hidden = false;
      } else {
        preview.removeAttribute('src');
        preview.hidden = true;
      }
    };
    paint(hotspot.imageUrl);
    // A signed URL that has expired since the config loaded would otherwise
    // sit as a broken-image glyph inside the editor's own panel.
    preview.addEventListener('error', () => paint(undefined));

    const choose = document.createElement('button');
    choose.type = 'button';
    choose.className = 'pill-button';
    choose.textContent = hotspot.imageUrl ? 'Trocar imagem' : 'Escolher imagem';

    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'pill-button';
    clear.textContent = 'Remover';
    clear.hidden = !hotspot.imageUrl;

    const applyPicked = (picked: PickedImage): void => {
      // The pointer is portal-owned and absent from InfoHotspot, so it is
      // written through a cast. The viewer ignores unknown keys; the portal
      // reads it to re-sign the URL once this one expires.
      //
      // Written BEFORE patch() rather than after: patch() is what notifies,
      // and a notification carrying imageUrl without its pointer describes a
      // state that must never be stored — a URL with no way back to the object.
      const raw = hotspot as unknown as Record<string, unknown>;
      if (picked.assetPath && picked.url) {
        raw[IMAGE_ASSET_PATH_KEY] = picked.assetPath;
        this.patch({ imageUrl: picked.url } as Partial<Hotspot>);
      } else {
        delete raw[IMAGE_ASSET_PATH_KEY];
        this.patch({ imageUrl: undefined, imageAlt: undefined } as Partial<Hotspot>);
      }
      // Re-render so the alt field disappears with the picture it described.
      this.openFor(hotspot);
    };

    const run = (): void => {
      const picker = this.pickImage;
      if (!picker) return;
      choose.disabled = true;
      clear.disabled = true;
      const previous = choose.textContent;
      choose.textContent = 'Aguardando o portal…';
      picker(hotspot)
        .then(applyPicked)
        .catch(() => {
          // The portal never answered. Say so: the likeliest cause is a portal
          // build with no picker, in which case the request reached nothing —
          // and staying silent about that is indistinguishable from the feature
          // being broken. It cost a support round trip once already.
          showToast('O portal não respondeu ao pedido de imagem.', { tone: 'error' });
        })
        .finally(() => {
          choose.disabled = false;
          clear.disabled = false;
          choose.textContent = previous;
        });
    };

    choose.addEventListener('click', run);
    clear.addEventListener('click', () => applyPicked({ assetPath: null, url: null }));

    const row = document.createElement('div');
    row.className = 'editor-panel__image-actions';
    row.append(choose, clear);
    wrap.append(preview, row);
    return wrap;
  }

  private textInput(value: string, onInput: (v: string) => void): HTMLInputElement {
    const el = document.createElement('input');
    el.type = 'text';
    el.value = value;
    el.className = 'editor-panel__input';
    el.addEventListener('input', () => onInput(el.value));
    return el;
  }

  private textareaInput(value: string, onInput: (v: string) => void): HTMLTextAreaElement {
    const el = document.createElement('textarea');
    el.value = value;
    el.rows = 3;
    el.className = 'editor-panel__input editor-panel__input--multiline';
    el.addEventListener('input', () => onInput(el.value));
    return el;
  }

  private numberInput(
    value: number,
    min: number,
    max: number,
    step: number,
    onInput: (v: number) => void,
  ): HTMLInputElement {
    const el = document.createElement('input');
    el.type = 'number';
    el.value = String(value);
    el.min = String(min);
    el.max = String(max);
    el.step = String(step);
    el.className = 'editor-panel__input editor-panel__input--number';
    el.addEventListener('input', () => {
      const v = Number(el.value);
      if (Number.isFinite(v)) onInput(v);
    });
    return el;
  }

  private fieldRow(label: string, control: HTMLElement): HTMLElement {
    const row = document.createElement('label');
    row.className = 'editor-panel__row';
    const lab = document.createElement('span');
    lab.className = 'editor-panel__label';
    lab.textContent = label;
    row.appendChild(lab);
    row.appendChild(control);
    return row;
  }
}

/**
 * Re-shape a hotspot when its type changes. Preserves shared fields (id,
 * label, yaw, pitch, distance) and stubs in the new type's required fields.
 */
function morphHotspotType(prev: Hotspot, nextType: HotspotType): Hotspot {
  const base = {
    id: prev.id,
    label: prev.label,
    yaw: prev.yaw,
    pitch: prev.pitch,
    distance: prev.distance,
  };
  switch (nextType) {
    case 'info':
      return { ...base, type: 'info', description: '' };
    case 'navigation':
      return { ...base, type: 'navigation', targetRoomId: '' };
    case 'variant':
      return { ...base, type: 'variant', targetVariantId: '' };
    case 'cta':
      return { ...base, type: 'cta', ctaLabel: 'Saiba mais' };
    case 'video':
      return { ...base, type: 'video' };
    case 'surface':
      return { ...base, type: 'surface', swatches: [] };
    case 'note':
      // Notes are buyer-authored at runtime, not configured via the editor.
      // The morph still needs to return *something* if an operator picks it
      // from the type dropdown — stub fields stay editable inline.
      return { ...base, type: 'note', text: '', updatedAt: new Date().toISOString(), roomId: '' };
  }
}
